/**
 * The strategy controls in the chain header.
 *
 * Three things in one group: which strategy is armed, and the two directions
 * it can be fired in.
 *
 * NEITHER BUTTON PLACES ANYTHING. They open the confirmation with an INTENT
 * already chosen — whether the saved legs get flipped to express it is the
 * picker's decision, from `shouldInvert` (5.21). `StrategyPicker` is where the resolved legs and the
 * book-priced net premium are shown, and where a LIVE account demands the
 * typed word — a header button that fired a four-leg basket straight onto a
 * live account would route around the only confirmation in the product.
 */

import { TrendingDown, TrendingUp } from 'lucide-react'
import type { ExpiryKind, TradeIntent } from '@apna/shared'
import type { StrategyDto } from '@/types/strategy'
import type { PreviewChainRow } from '@/hooks/useStrategyPreviews'
import { StrategyDropdown } from './StrategyDropdown'
import { structureLabel } from './strategyLabel'

export interface StrategyBarProps {
  strategy: StrategyDto | null
  onChange: (strategy: StrategyDto) => void
  /**
   * WHAT THE TRADER WANTS, not what to do to the legs.
   *
   * Whether the saved legs are flipped is derived from the structure itself
   * — see `shouldInvert`. A button that named a transformation is what put a
   * long straddle behind a red Sell button.
   */
  onFire: (intent: TradeIntent) => void
  rows: readonly PreviewChainRow[]
  /** Which expiry kinds `rows` answers to — see `useStrategyPreviews`. */
  chainKinds?: readonly ExpiryKind[]
  spot: number
  step: number
  lotSize: number
  /** The header's size. The dropdown prices every strategy at it. */
  lots: number
  /**
   * The size control, rendered immediately after the strategy selector.
   *
   * A SLOT rather than a sibling, because this bar owns a `flex-1` spacer
   * that pushes the fire buttons to the right edge — anything placed beside
   * the bar lands on the far side of that spacer, past the buttons. What you
   * are trading and how much of it belong together, and both belong before
   * the controls that send it.
   */
  sizeControl?: React.ReactNode
  /** Compact display controls, before the execution buttons in the same row. */
  viewControl?: React.ReactNode
  /** Open the basket builder — 9.66. Passed through to the dropdown's first row. */
  onBuildBasket?: () => void
}

export function StrategyBar({
  strategy,
  onChange,
  onFire,
  rows,
  chainKinds,
  spot,
  step,
  lotSize,
  lots,
  sizeControl,
  viewControl,
  onBuildBasket,
}: StrategyBarProps) {
  const label = strategy ? structureLabel(strategy.name) : 'Strategy'

  return (
    /*
      WRAPS, and `min-w-0` lets it. This row sits inside `.tr-hdr`, which
      wraps — but a nested flex row does not inherit that, so with the
      drawer open everything here was forced onto one line and the fire
      buttons were clipped off the card's right edge. A control that cannot
      be reached is worse than one on a second line.
    */
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
      <StrategyDropdown
        value={strategy}
        onChange={onChange}
        rows={rows}
        chainKinds={chainKinds}
        spot={spot}
        step={step}
        lotSize={lotSize}
        lots={lots}
        onBuildBasket={onBuildBasket}
      />

      {sizeControl}

      {/*
        Direction. Each button states an intent; the picker flips the legs
        only when the structure is not already written that way — so the
        pair reads as "sell this shape" / "buy this shape" rather than as two
        unrelated templates.

        Tinted rather than solid. These are the two most consequential
        controls on the screen, but they sit beside 66 solid B/S buttons in
        the ladder below; two more blocks of flat red and green at this size
        drowned those out. The arrow carries the direction at a glance and
        the fill still separates them from everything else in the row.

        THE LABEL COLLAPSES TO ONE LETTER in a narrow header rather than
        wrapping to a second row — see `.tr-fire` in terminalRedesign.css. It
        is driven by a CONTAINER query on the chain card, not a viewport media
        query: the width that decides this is the chain COLUMN's, and that
        halves when the drawer opens on a monitor no media query would call
        narrow. The accessible name stays the full sentence either way.
      */}
      <div className="flex-1" />

      {viewControl}

      <button
        type="button"
        onClick={() => onFire('SELL')}
        disabled={!strategy}
        data-testid="fire-sell-strategy"
        aria-label={`Sell ${strategy?.name ?? 'strategy'}`}
        className="tr-fire sell"
      >
        <TrendingDown size={14} className="tr-fire-icon" />
        <span className="tr-fire-long">Sell <span className="tr-fire-strategy">{label}</span></span>
        <span className="tr-fire-short">S</span>
      </button>
      <button
        type="button"
        onClick={() => onFire('BUY')}
        disabled={!strategy}
        data-testid="fire-buy-strategy"
        aria-label={`Buy ${strategy?.name ?? 'strategy'}`}
        className="tr-fire buy"
      >
        <TrendingUp size={14} className="tr-fire-icon" />
        <span className="tr-fire-long">Buy <span className="tr-fire-strategy">{label}</span></span>
        <span className="tr-fire-short">B</span>
      </button>
    </div>
  )
}
